import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router';
import { z } from 'zod';
import { t } from '@budgeting101/i18n';
import { useAuthStore, type WorkspaceCapabilityState } from '../hooks/useAuth.js';
import { api } from '../lib/api.js';
import { scopedQueryKey } from '../lib/queryKeys.js';
import {
  SharedGroupDetailSchema,
  SharedGroupListResponseSchema,
  type SharedGroupDetail,
  type SharedGroupSummary,
} from '../routes/shared-group-contracts.js';

const READABLE_CAPABILITY_STATES = new Set<WorkspaceCapabilityState>([
  'READY',
  'SUSPENDED',
  'ARCHIVED',
]);
const GroupIdSchema = z.string().uuid();

export type SharedGroupPage =
  | 'overview'
  | 'expenses'
  | 'record'
  | 'balances'
  | 'settle'
  | 'budget'
  | 'activity'
  | 'people'
  | 'evidence'
  | 'settings';

export interface SharedNavigationItem {
  key: string;
  href: string;
  label: string;
  shortLabel: string;
  icon: string;
  end?: boolean;
}

interface SharedDestination {
  key: SharedGroupPage;
  segment: string;
  labelKey: string;
  shortLabelKey: string;
  icon: string;
  anyPermission?: readonly string[];
  activeOnly?: boolean;
}

const SHARED_DESTINATIONS: readonly SharedDestination[] = [
  { key: 'overview', segment: '', labelKey: 'auth.overview', shortLabelKey: 'auth.overview', icon: 'space_dashboard' },
  { key: 'expenses', segment: 'expenses', labelKey: 'auth.expenses', shortLabelKey: 'auth.expenses', icon: 'receipt_long', anyPermission: ['shared.expenses.view'] },
  { key: 'record', segment: 'expenses/new', labelKey: 'auth.recordExpense', shortLabelKey: 'nav.record', icon: 'add_circle', anyPermission: ['shared.expenses.record'], activeOnly: true },
  { key: 'balances', segment: 'balances', labelKey: 'auth.balances', shortLabelKey: 'auth.balances', icon: 'balance', anyPermission: ['shared.expenses.view'] },
  { key: 'settle', segment: 'settle', labelKey: 'auth.settleUp', shortLabelKey: 'auth.settle', icon: 'swap_horiz', anyPermission: ['shared.settlements.preview'] },
  { key: 'budget', segment: 'budget', labelKey: 'auth.budgets', shortLabelKey: 'auth.budgets', icon: 'account_balance_wallet', anyPermission: ['shared.budgets.view', 'shared.budgets.manage'] },
  { key: 'activity', segment: 'activity', labelKey: 'auth.activity', shortLabelKey: 'auth.activity', icon: 'history', anyPermission: ['shared.activity.view'] },
  { key: 'people', segment: 'people', labelKey: 'auth.people', shortLabelKey: 'auth.people', icon: 'group' },
  { key: 'evidence', segment: 'evidence', labelKey: 'auth.evidence', shortLabelKey: 'auth.evidence', icon: 'attach_file', anyPermission: ['shared.evidence.view', 'shared.evidence.attach'] },
  { key: 'settings', segment: 'settings', labelKey: 'auth.groupSettings', shortLabelKey: 'auth.settings', icon: 'settings' },
];

function destinationHref(groupId: string, segment: string): string {
  return `/shared/groups/${groupId}${segment ? `/${segment}` : ''}`;
}

export function sharedGroupIdFromPath(pathname: string): string | null {
  const candidate = pathname.match(/^\/shared\/groups\/([^/]+)(?:\/|$)/)?.[1];
  const parsed = GroupIdSchema.safeParse(candidate);
  return parsed.success ? parsed.data : null;
}

export function sharedGroupPageFromPath(pathname: string, groupId: string): SharedGroupPage | null {
  const prefix = `/shared/groups/${groupId}`;
  if (!pathname.startsWith(prefix)) return null;
  const suffix = pathname.slice(prefix.length).replace(/^\/+|\/+$/g, '');
  if (!suffix) return 'overview';
  return SHARED_DESTINATIONS.find((destination) => destination.segment === suffix)?.key ?? null;
}

async function listAllActiveGroups(): Promise<SharedGroupSummary[]> {
  const groups = new Map<string, SharedGroupSummary>();
  const seenCursors = new Set<string>();
  let cursor: string | null = null;

  for (let pageNumber = 0; pageNumber < 100; pageNumber += 1) {
    const response: z.infer<typeof SharedGroupListResponseSchema> = await api.get('/shared/groups', SharedGroupListResponseSchema, {
      limit: 50,
      view: 'ACTIVE',
      ...(cursor ? { cursor } : {}),
    });
    response.items.forEach((group) => groups.set(group.id, group));
    if (!response.page.hasMore || !response.page.nextCursor) return [...groups.values()];
    if (seenCursors.has(response.page.nextCursor)) {
      throw new Error('The Shared group directory returned a repeated page cursor.');
    }
    seenCursors.add(response.page.nextCursor);
    cursor = response.page.nextCursor;
  }

  throw new Error('The Shared group directory exceeded the supported page limit.');
}

function canOpenDestination(destination: SharedDestination, group: SharedGroupDetail | undefined): boolean {
  if (destination.activeOnly && group?.status !== 'ACTIVE') return false;
  if (!destination.anyPermission) return true;
  if (!group) return false;
  return destination.anyPermission.some((permission) => group.effectivePermissions.includes(permission));
}

export interface SharedNavigationModel {
  currentGroupId: string | null;
  currentGroup: SharedGroupDetail | undefined;
  currentPage: SharedGroupPage | null;
  groups: SharedGroupSummary[];
  groupsLoading: boolean;
  groupsUnavailable: boolean;
  workspaceItems: SharedNavigationItem[];
  groupItems: SharedNavigationItem[];
  mobilePrimaryItems: SharedNavigationItem[];
  mobileMoreItems: SharedNavigationItem[];
  switchGroup: (groupId: string) => void;
}

export function useSharedNavigationModel(enabled: boolean): SharedNavigationModel {
  const location = useLocation();
  const navigate = useNavigate();
  const activeBook = useAuthStore((state) => state.activeBook);
  const capabilities = useAuthStore((state) => state.capabilities);
  const permissions = useAuthStore((state) => state.permissions);
  const currentGroupId = sharedGroupIdFromPath(location.pathname);
  const currentPage = currentGroupId
    ? sharedGroupPageFromPath(location.pathname, currentGroupId)
    : null;
  const canReadGroups = capabilities.some((capability) => (
    capability.key === 'shared.groups'
    && READABLE_CAPABILITY_STATES.has(capability.state)
  ));
  const hasActiveGroupBook = Boolean(
    activeBook
    && activeBook.kind === 'GROUP_OBLIGATION'
    && activeBook.status === 'ACTIVE',
  );
  const canCreateGroups = permissions.includes('shared.groups.create')
    && capabilities.some((capability) => (
      capability.key === 'shared.groups' && capability.state === 'READY'
    ))
    && hasActiveGroupBook
    && activeBook?.readiness === 'READY';

  const groupsQuery = useQuery({
    queryKey: scopedQueryKey(['shared-groups', 'navigation', 'active']),
    queryFn: listAllActiveGroups,
    enabled: enabled && canReadGroups && hasActiveGroupBook,
    staleTime: 30_000,
  });
  const currentGroupQuery = useQuery({
    queryKey: scopedQueryKey(['shared-groups', 'detail', currentGroupId]),
    queryFn: () => api.get(`/shared/groups/${currentGroupId!}`, SharedGroupDetailSchema),
    enabled: enabled && currentGroupId !== null && canReadGroups && hasActiveGroupBook,
  });

  const groupsById = new Map((groupsQuery.data ?? []).map((group) => [group.id, group]));
  if (currentGroupQuery.data) groupsById.set(currentGroupQuery.data.id, currentGroupQuery.data);
  const groups = [...groupsById.values()];
  const visibleDestinations = currentGroupId
    ? SHARED_DESTINATIONS.filter((destination) => canOpenDestination(destination, currentGroupQuery.data))
    : [];
  const groupItems: SharedNavigationItem[] = visibleDestinations.map((destination) => ({
    key: destination.key,
    href: destinationHref(currentGroupId!, destination.segment),
    label: t(destination.labelKey),
    shortLabel: t(destination.shortLabelKey),
    icon: destination.icon,
    end: true,
  }));
  const workspaceItems: SharedNavigationItem[] = [
    { key: 'groups', href: '/', label: t('auth.myGroups'), shortLabel: t('auth.myGroups'), icon: 'group', end: true },
    ...(canCreateGroups
      ? [{ key: 'new-group', href: '/shared/new', label: t('auth.createGroup'), shortLabel: t('auth.createGroup'), icon: 'group_add', end: true }]
      : []),
  ];
  const preferredMobileKeys: readonly SharedGroupPage[] = ['overview', 'activity', 'record', 'settle'];
  const preferredMobile = preferredMobileKeys
    .map((key) => groupItems.find((item) => item.key === key))
    .filter((item): item is SharedNavigationItem => item !== undefined);
  const mobilePrimaryItems = currentGroupId
    ? [
        ...preferredMobile,
        ...groupItems.filter((item) => !preferredMobile.some((candidate) => candidate.key === item.key)),
      ].slice(0, 4)
    : workspaceItems.slice(0, 2);
  const primaryKeys = new Set(mobilePrimaryItems.map((item) => item.key));
  const mobileMoreItems = currentGroupId
    ? [...workspaceItems, ...groupItems.filter((item) => !primaryKeys.has(item.key))]
    : workspaceItems.slice(2);

  function switchGroup(groupId: string): void {
    const parsed = GroupIdSchema.safeParse(groupId);
    if (!parsed.success) return;
    const currentDestination = SHARED_DESTINATIONS.find((destination) => destination.key === currentPage);
    navigate(destinationHref(parsed.data, currentDestination?.segment ?? ''));
  }

  return {
    currentGroupId,
    currentGroup: currentGroupQuery.data,
    currentPage,
    groups,
    groupsLoading: groupsQuery.isLoading,
    groupsUnavailable: groupsQuery.isError,
    workspaceItems,
    groupItems,
    mobilePrimaryItems,
    mobileMoreItems,
    switchGroup,
  };
}

export function SharedGroupSwitcher({
  model,
  compact = false,
}: {
  model: SharedNavigationModel;
  compact?: boolean;
}) {
  const currentLabel = model.currentGroup?.name.trim() || t('sharedSwitcher.current');
  return (
    <div className={`shared-group-switcher${compact ? ' shared-group-switcher--compact' : ''}`}>
      <label>
        <span>{t('sharedSwitcher.label')}</span>
        <select
          aria-label={t('sharedSwitcher.switchLabel')}
          value={model.currentGroupId ?? ''}
          disabled={model.groupsLoading || (model.groups.length === 0 && !model.currentGroupId)}
          onChange={(event) => model.switchGroup(event.target.value)}
        >
          {!model.currentGroupId && <option value="">{t('sharedSwitcher.choose')}</option>}
          {model.currentGroupId && !model.groups.some((group) => group.id === model.currentGroupId) && (
            <option value={model.currentGroupId}>{currentLabel}</option>
          )}
          {model.groups.map((group) => (
            <option key={group.id} value={group.id}>
              {group.name.trim() || t('sharedSwitcher.unnamed')} · {group.summaryCurrency}
            </option>
          ))}
        </select>
      </label>
      {!compact && (
        <small role={model.groupsUnavailable ? 'alert' : undefined}>
          {model.groupsUnavailable
            ? t('sharedSwitcher.unavailable')
            : model.groupsLoading
              ? t('sharedSwitcher.loading')
              : t('sharedSwitcher.available', { count: model.groups.length })}
        </small>
      )}
    </div>
  );
}
